import { createContext, useState, useReducer, useRef, useEffect } from "react";
import * as signalR from '@microsoft/signalr';
import * as msgpack from '@microsoft/signalr-protocol-msgpack';
import AlertAlgo from '@controls/AlertSnack';
import i18next from 'i18next';
import * as storage from '@utils/storage'
import { actionTypes } from '@utils/constants';
import { formatTimestamp } from '@utils/index';
import { Token, ChatId, Company, UserAssociation, IsEmployeeMode } from '@users';
import { getSettings, getEmployeeUsers, updateReadDatePrivateMessage, getInitials, sortLatestUsersMessage, sortMessages } from '@actions/chat';
import * as StatusCode from '@scripts/StatusCode';

// Define initial state
const initialState = {
    connection: null,
    counter: 0,
    messages: [],
    latestUsersMessage: [],
    usersStatus: [],
    status: 'disconnected',
    genericError: null,
    customError: null,
    currentChatUser: null
};

// Define reducer
const connectionReducer = (state, action) => {
    switch (action.type) {
        case actionTypes.CONNECTING:
            return { ...state, status: 'connecting' };
        case actionTypes.CONNECTED:
            return { ...state, connection: action.payload, status: 'connected' };
        case actionTypes.CONNECTED_USERS:
            return { ...state, usersStatus: action.payload };
        case actionTypes.DISCONNECTED:
            return { ...state, connection: null, status: 'disconnected' };
        case actionTypes.GENERICERROR: {
            return { ...state, genericError: action.payload };
        }
        case actionTypes.CUSTOMERROR: {
            return { ...state, customError: action.payload };
        }
        case actionTypes.CHAT_HISTORY: { 

            const messages = action.payload;
            const counter = action.payload.filter(x => x.readDate === null && x.recipient.id == ChatId()).length;

            const users = new Set(action.payload.map(message => {
                return message.author.id;
            }).filter(userId => userId !== ChatId()));

            const latestUsersMessage = [];
            users.forEach(user => {
                latestUsersMessage.push(action.payload.filter(message => message.author.id === user).sort((msg1, msg2) => {
                    return new Date(msg2.timestamp) - new Date(msg1.timestamp);
                })[0]);
            });

            return { ...state, messages, latestUsersMessage: sortLatestUsersMessage(latestUsersMessage), counter };
        }
        case actionTypes.LOAD_PREV_CHAT: {
            const messages = state.messages.concat(action.payload);
            return { ...state, messages: sortMessages(messages) };
        }
        case actionTypes.SEND_MESSAGE:
            return { ...state, messages: [...state.messages, action.payload] };
        case actionTypes.RECEIVED_MESSAGE: {
            const messages = [...state.messages, action.payload];
            const counter = action.payload.author.id != state.currentChatUser ? state.counter + 1 : state.counter;
            const latestUsersMessage = state.latestUsersMessage.map(message =>
                ((message.author.id === action.payload.author.id)
                    && (new Date(message.timestamp) <= new Date(action.payload.timestamp))) ?
                    action.payload :
                    message
            );

            if (state.latestUsersMessage.filter(message => message.author.id === action.payload.author.id).length == 0) {
                latestUsersMessage.push(action.payload);
            }

            return { ...state, messages, latestUsersMessage: sortLatestUsersMessage(latestUsersMessage), counter };
        }
        case actionTypes.READ_MESSAGE: {

            // Get current chat user
            const currentChatUser = action.payload;

            // Get all unread messages for current chat user
            const counter = state.counter - state.messages.filter(x => x.readDate === null && x.author.id == currentChatUser).length;

            // Update read date for current chat user
            const latestUsersMessage = state.latestUsersMessage.map(message => {
                if (message.readDate === null
                    && message.author.id == currentChatUser) {
                    return { ...message, readDate: new Date() };
                }
                return message;
            });

            const messages = state.messages.map(message => {
                if (message.readDate === null
                    && message.author.id == currentChatUser) {
                    return { ...message, readDate: new Date() };
                }
                return message;
            });

            return { ...state, currentChatUser, latestUsersMessage: sortLatestUsersMessage(latestUsersMessage), messages, counter };
        }
        case actionTypes.UNREAD_MESSAGE:
            return { ...state, currentChatUser: null };
        default:
            return state;
    }
};

// Create context
export const ChatContext = createContext();

export const ChatProvider = ({ children }) => {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [chatUsers, setChatUsers] = useState([]);
    const [state, dispatch] = useReducer(connectionReducer, initialState);
    const alertRef = useRef(null);

    const showMessageError = (message) => {
        alertRef.current.showMessageError(message);
    }

    const showMessageSuccess = (message) => {
        alertRef.current.showMessageSuccess(message);
    }

    useEffect(() => {
        
        if (storage.getIsDisplayChatMenu()
            && IsEmployeeMode()) {// TODO : delete IsEmployeeMode when supervisor mode ready

            getEmployeeUsers().then(async (response) => {
                if (response == undefined) {
                    dispatch({ type: actionTypes.GENERICERROR, payload: 'No response from server.' });
                    return;
                } else if (response?.status == StatusCode.STATUS_OK) {
                    setChatUsers(response.data.map(user => {
                        user.initial = getInitials(user.fullName);
                        return user;
                    }));
                }
            })
                .catch(error => {
                    dispatch({ type: actionTypes.GENERICERROR, payload: error });
                });
        }
    }, []);

    useEffect(() => {        
        if (state.genericError) {
            //console.log(state.genericError);
            showMessageError(i18next.t('error:generic-error'));
        }        
    }, [state.genericError]);

    useEffect(() => {
        if (state.customError) {
            showMessageError(state.customError);
        }        
    }, [state.customError]);

    useEffect(() => {
        if (storage.getIsDisplayChatMenu()
            && IsEmployeeMode()// TODO : delete IsEmployeeMode when supervisor mode ready 
            && state.connection
            && state.connection.state === signalR.HubConnectionState.Connected) {

            const handleReceiveMessage = (message, senderId, chatId) => {

                const senderObj = chatUsers.filter(x => senderId == (x.userCode + x.konekUserAssociationId + Company()))[0];
                const readDate = state.currentChatUser == senderId ? new Date() : null;
                const senderName = senderObj.fullName;

                if (state.currentChatUser == senderId) {
                    updateReadDatePrivateMessage(senderObj.konekUserAssociationId, UserAssociation()).then(async (response) => {
                        if (response == undefined) {
                            dispatch({ type: actionTypes.GENERICERROR, payload: 'No response from server' });
                            return;
                        } else if (response.status === true) {
                            dispatch({
                                type: actionTypes.RECEIVED_MESSAGE,
                                payload: {
                                    id: chatId,
                                    author: {
                                        id: senderId,
                                        name: (senderId !== ChatId() ? senderName + ' ' : '') + formatTimestamp(new Date()),
                                        initials: getInitials(senderName)
                                    },
                                    recipient: {
                                        id: ChatId(),
                                        initials: getInitials(storage.getUserName())
                                    },
                                    timestamp: new Date(),
                                    text: message,
                                    readDate,
                                    isFirstChat: state.messages.filter(x => (x.author.id == senderId
                                        && x.recipient.id == ChatId())
                                        || (x.author.id == ChatId()
                                            && x.recipient.id == senderId)).length == 0 ? true : false
                                }
                            });
                        }
                    })
                        .catch(error => {
                            dispatch({ type: actionTypes.GENERICERROR, payload: error });
                        });
                } else {
                    dispatch({
                        type: actionTypes.RECEIVED_MESSAGE,
                        payload: {
                            id: chatId,
                            author: {
                                id: senderId,
                                name: (senderId !== ChatId() ? senderName + ' ' : '') + formatTimestamp(new Date()),
                                initials: getInitials(senderName)
                            },
                            recipient: {
                                id: ChatId(),
                                initials: getInitials(storage.getUserName())
                            },
                            timestamp: new Date(),
                            text: message,
                            readDate,
                            isFirstChat: state.messages.filter(x => (x.author.id == senderId
                                && x.recipient.id == ChatId())
                                || (x.author.id == ChatId()
                                    && x.recipient.id == senderId)).length == 0 ? true : false
                        }
                    });
                }
            };

            state.connection.on("ReceiveMessage", handleReceiveMessage);

            // Cleanup on unmount
            return () => {
                state.connection.off("ReceiveMessage", handleReceiveMessage);
            };
        }
    }, [state.connection, state.currentChatUser, state.messages]);

    useEffect(() => {
        const connect = async () => {
            try {
                getSettings().then(async (response) => {

                    if (response == undefined) {
                        dispatch({ type: actionTypes.GENERICERROR, payload: 'No response from server.' });
                        return;
                    }

                    if (response.status === false && response.errorMsg.length > 0) {
                        dispatch({ type: actionTypes.CUSTOMERROR, payload: response.errorMsg });
                    } else if (response.chatApiUrl) {

                        dispatch({ type: actionTypes.CONNECTING });
                        const connection = new signalR.HubConnectionBuilder()
                            .withUrl(response.chatApiUrl, {
                                accessTokenFactory: () => Token(),
                                skipNegotiation: true,
                                transport: signalR.HttpTransportType.WebSockets
                            })
                            .withHubProtocol(new msgpack.MessagePackHubProtocol())
                            .withAutomaticReconnect()
                            .configureLogging(signalR.LogLevel.None)
                            .build();

                        connection.onclose((error) => {
                            dispatch({ type: actionTypes.DISCONNECTED });
                            dispatch({ type: actionTypes.GENERICERROR, payload: 'Connection closed. Try refreshing the page or check network issues : ' + error });
                        });

                        connection.start()
                            .then(() => {
                                dispatch({ type: actionTypes.CONNECTED, payload: connection });

                                connection.on("ChatHistory", (chatsResult) => {
                                    const messages = [];
                                    chatsResult.forEach(chat => {

                                        const dispachDate = chat.DispachDate !== null ? new Date(chat.DispachDate.toLocaleString()) : chat.DispachDate;
                                        const readDate = chat.ReadDate !== null ? new Date(chat.ReadDate.toLocaleString()) : chat.ReadDate;
                                        const senderId = chat.SenderUserCode + chat.SenderId + Company();
                                        const senderName = (senderId !== ChatId() ? chat.SenderFullName + ' ' : '') + formatTimestamp(dispachDate);
                                        const recipientId = chat.RecipientUserCode + chat.RecipientId + Company();

                                        messages.push(
                                            {
                                                id: chat.ChatId,
                                                author: {
                                                    id: senderId,
                                                    name: senderName,
                                                    initials: getInitials(chat.SenderFullName)
                                                },
                                                recipient: {
                                                    id: recipientId,
                                                    initials: getInitials(chat.RecipientFullName)
                                                },
                                                timestamp: dispachDate,
                                                text: chat.MessageBody,
                                                readDate,
                                                isFirstChat: chat.FirstChat == 'O'
                                            }
                                        );
                                    });

                                    dispatch({ type: actionTypes.CHAT_HISTORY, payload: messages });
                                });

                                connection.on("ConnectedUsers", (users) => {
                                    dispatch({ type: actionTypes.CONNECTED_USERS, payload: users });
                                });

                                connection.onreconnecting(error => {
                                    dispatch({ type: actionTypes.CUSTOMERROR, payload: i18next.t('chat:error.onReconnecting') });
                                });

                                connection.onreconnected(connectionId => {
                                    showMessageSuccess(i18next.t('chat:display.onReconnected'));
                                });
                            })
                            .catch(error => {
                                dispatch({ type: actionTypes.GENERICERROR, payload: error });
                            });
                    }
                })
                    .catch(error => {
                        dispatch({ type: actionTypes.GENERICERROR, payload: error });
                    });
            } catch (error) {
                dispatch({ type: actionTypes.GENERICERROR, payload: error });
            }
        };

        const disconnect = async () => {
            if (state.connection
                && state.connection.state === signalR.HubConnectionState.Connected) {
                try {
                    await connection.stop();
                    dispatch({ type: actionTypes.DISCONNECTED });
                } catch (error) {
                    dispatch({ type: actionTypes.GENERICERROR, payload: error });
                }
            }
        };

        // Respond to authentication state changes
        if (storage.getIsDisplayChatMenu()
            && IsEmployeeMode()) {// TODO : delete IsEmployeeMode when supervisor mode ready 
            if (isAuthenticated) {
                connect();
            } else {
                disconnect();
            }
        }

        // Cleanup on unmount
        return () => {
            if (storage.getIsDisplayChatMenu()
                && IsEmployeeMode()) { // TODO : delete IsEmployeeMode when supervisor mode ready 
                disconnect();
            }            
        };

    }, [isAuthenticated]);

    return (
        <div>
            <ChatContext.Provider value={{ isAuthenticated, setIsAuthenticated, chatUsers, state, dispatch}}>
                {children}
            </ChatContext.Provider>
            <AlertAlgo ref={alertRef} />
        </div>
    );
};
